import React, { MouseEventHandler } from 'react'
import { colors } from '../../Theme/colors'
import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import { css } from '@emotion/react'

type Props = {
  variant: 'outlined' | 'contained' | 'borderless'
  disabled?: boolean
  size?: 'small' | 'large'
  children: React.ReactNode
  icon?: React.ReactNode
  iconType?: 'leading' | 'trailing' | 'none'
  clickHandler?: MouseEventHandler
  type?: 'button' | 'submit' | 'reset'
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    borderless: true
  }
}

/**
 * @usage
 * @important - THIS COMPONENT IS WORK IN PROGRESS
 * Generic Button component with Resilia styling.
 *
 * @see https://www.figma.com/file/ZTE3cJou0ON1Na6zZB44L5/Birdsong-Version-1.2?node-id=2%3A116&t=1Lgh8UgZM7y6NWcd-4
 * @param {string} variant contained | outlined | ...
 * @param {boolean} disabled if the button is disabled
 * @param {ReactNode} children put the button text here
 * @param {MouseEventHandler} clickHandler function called on the onClick event
 * @param {ReactNode} icon icon to be added to the button
 * @param {string} iconType leading | trailing | none
 */

export const ButtonSong: React.FC<Props> = ({
  variant,
  children,
  disabled,
  icon,
  iconType,
  size = 'large',
  clickHandler,
}) => (
  <StyledButton
    variant={variant}
    size={size}
    disabled={disabled}
    onClick={clickHandler}
    disableFocusRipple
    startIcon={icon && iconType === 'leading' && icon}
    endIcon={icon && iconType === 'trailing' && icon}
    iconType={iconType}
  >
    {icon && iconType === 'none' && icon}
    {children}
  </StyledButton>
)

const StyledButton = styled(Button, {
  // add props here that you don't want forwarded to the base component (button)
  shouldForwardProp: (prop) => prop !== 'iconType',
})<Props>`
  flex-shrink: 0;
  ${(props) => props.size && sizeStyles[props.size]};
  ${(props) => props.variant && variantStyles[props.variant]};
  ${(props) => props.disabled && disabledStyles[props.variant]};
  ${(props) => props.iconType && iconStyles[props.iconType]};
`

const variantStyles = {
  contained: css`
    background-color: ${colors.resiliaBlue};
    color: ${colors.resiliaWhite};
    &:focus {
      background-color: ${colors.navy};
      box-shadow: 0px 0px 4px ${colors.resiliaBlue};
    }
  `,
  outlined: css`
    color: ${colors.resiliaBlue};
    &:hover {
      background-color: ${colors.lightestBlue};
    }
    &:focus {
      background-color: ${colors.lightestBlue};
      box-shadow: 0px 0px 4px ${colors.resiliaBlue};
    }
  `,
  borderless: css`
    border-color: none;
    &:hover {
      background-color: ${colors.lightestBlue};
    }
    &:focus {
      background-color: ${colors.lightestBlue};
      box-shadow: 0px 0px 4px ${colors.resiliaBlue};
    }
  `,
}

const disabledStyles = {
  outlined: css`
    border-color: ${colors.grey} !important;
    color: ${colors.darkGrey} !important;
  `,
  contained: css`
    color: ${colors.darkGrey} !important;
  `,
  borderless: css`
    color: ${colors.darkGrey} !important;
  `,
}

const iconStyles = {
  leading: css`
    > span > svg {
      height: 17px;
      width: 17px;
      padding-left: 5px;
    }
  `,
  trailing: css`
    > span > svg {
      height: 17px;
      width: 17px;
      padding-right: 5px;
    }
  `,
  none: css`
    > svg {
      height: 17px;
      width: 17px;
    }
  `,
}

const sizeStyles = {
  large: css`
    padding: 12px 16px;
    font-size: 16px;
    line-height: 135%;
    min-width: 56px;
    max-height: 48px;
  `,
  small: css`
    padding: 8px 16px;
    font-size: 14px;
    line-height: 165%;
    min-width: 56px;
    max-height: 40px;
  `,
}
